import React from 'react';
import Popper, { PopperProps } from '@material-ui/core/Popper';
import Grow from '@material-ui/core/Grow';
import Paper from '@material-ui/core/Paper';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';

export type HandleClose = () => void;

interface ChildrenProps {
  handleClose: HandleClose;
}

interface OwnProps {
  anchor: React.ReactNode;
  children: (props: ChildrenProps) => React.ReactNode;
  isDisabled?: boolean;
  onBtnClick?: () => void;
  onClose?: HandleClose;
  onOpen?: () => void;
  openOnMouseEnter?: boolean;
}

type Props = OwnProps & Omit<PopperProps, 'open' | 'transition'>;

const PopperNew: React.FC<Props> = ({
  anchor,
  children,
  container,
  isDisabled,
  modifiers,
  onBtnClick,
  onClose,
  onOpen,
  openOnMouseEnter,
  placement,
}) => {
  const anchorElement = React.useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const handleClose = React.useCallback(() => {
    setIsOpen(false);
    if (onClose) {
      onClose();
    }
  }, [onClose, setIsOpen]);
  const handleOpen = React.useCallback(() => {
    if (isDisabled) {
      return;
    }
    setIsOpen(true);
    if (onOpen) {
      onOpen();
    }
  }, [isDisabled, onOpen, setIsOpen]);
  const handleClick = React.useCallback(() => {
    if (onBtnClick) {
      onBtnClick();
    }
    handleOpen();
  }, [handleOpen, onBtnClick]);
  const handleMouseEnter = React.useCallback(() => {
    if (openOnMouseEnter) {
      handleOpen();
    }
  }, [handleOpen, openOnMouseEnter]);

  /* eslint-disable jsx-a11y/click-events-have-key-events */
  /* eslint-disable jsx-a11y/no-static-element-interactions */
  return (
    <React.Fragment>
      <div
        id="anchor"
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        ref={anchorElement}
      >
        {anchor}
      </div>
      <Popper
        anchorEl={anchorElement.current}
        container={container}
        modifiers={modifiers}
        open={isOpen}
        // It's the same zIndex that MUI Modal has, so when we open MapPreview in SearchMap we can see it
        placement={placement}
        style={{ position: 'absolute', zIndex: 1300 }}
        transition
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Grow {...TransitionProps}>
              <Paper>{children({ handleClose })}</Paper>
            </Grow>
          </ClickAwayListener>
        )}
      </Popper>
    </React.Fragment>
  );
};

export default PopperNew;
